import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Send, Smile, Image, X, Upload } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface CommentFormProps {
  onSubmit: (content: string, attachments?: any[]) => Promise<void>;
  placeholder?: string;
  buttonText?: string;
  isReply?: boolean;
  userAvatar?: string;
  userName?: string;
  autoFocus?: boolean;
}

// Extended emoji collection
const emojiCategories = {
  prayer: ['🙏', '✝️', '⛪', '🕊️', '💒', '📿', '🤲'],
  hearts: ['❤️', '💕', '💖', '💝', '💗', '💙', '💚', '💛', '🧡', '💜'],
  celebration: ['🙌', '✨', '🎉', '🎊', '🌟', '⭐', '💫', '🎆'],
  support: ['🤗', '🫂', '💪', '👏', '👍', '✊', '🙌', '💯'],
  emotions: ['😊', '😢', '🥺', '😇', '😌', '🥰', '😍', '🤗'],
  nature: ['🌈', '🌸', '🌺', '🌻', '🌷', '🦋', '🕊️', '☀️']
};

export default function CommentForm({
  onSubmit,
  placeholder = "Share your thoughts...",
  buttonText = "Comment",
  isReply = false,
  userAvatar,
  userName,
  autoFocus = false
}: CommentFormProps) {
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<keyof typeof emojiCategories>('prayer');
  const [attachments, setAttachments] = useState<any[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onSubmit(content.trim(), attachments);
      setContent('');
      setAttachments([]);
    } catch (error) {
      console.error('Error submitting comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleEmojiClick = (emoji: string) => {
    setContent(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  const handleImageUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    
    setIsUploading(true);
    const uploadedFiles = [];

    try {
      for (const file of Array.from(files)) {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          toast({
            variant: "destructive",
            title: "Invalid file type",
            description: "Please upload only image files."
          });
          continue;
        }

        // Validate file size (5MB limit)
        if (file.size > 5 * 1024 * 1024) {
          toast({
            variant: "destructive", 
            title: "File too large",
            description: "Please upload images smaller than 5MB."
          });
          continue;
        }

        // Upload to Supabase Storage with correct path structure: {user_id}/comments/{filename}
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          toast({
            variant: "destructive",
            title: "Authentication required",
            description: "Please sign in to upload images."
          });
          continue;
        }
        
        const fileName = `${user.id}/comments/${Date.now()}-${file.name}`;
        const { data, error } = await supabase.storage
          .from('prayer-attachments')
          .upload(fileName, file);

        if (error) {
          console.error('Error uploading file:', error);
          toast({
            variant: "destructive",
            title: "Upload failed",
            description: error.message || "Failed to upload image. Please try again."
          });
          continue;
        }

        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from('prayer-attachments')
          .getPublicUrl(fileName);

        uploadedFiles.push({
          type: 'image',
          url: publicUrl,
          name: file.name,
          size: file.size
        });
      }

      setAttachments(prev => [...prev, ...uploadedFiles]);
      
      if (uploadedFiles.length > 0) {
        toast({
          title: "Images uploaded",
          description: `${uploadedFiles.length} image(s) uploaded successfully.`
        });
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      toast({
        variant: "destructive",
        title: "Upload error",
        description: "An error occurred while uploading images."
      });
    } finally {
      setIsUploading(false);
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const characterCount = content.length;
  const maxLength = 500;
  const isOverLimit = characterCount > maxLength;

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="flex gap-3">
        {userAvatar && (
          <Avatar className="h-8 w-8 flex-shrink-0">
            <AvatarImage src={userAvatar} alt={userName || 'User'} />
            <AvatarFallback>
              {userName?.charAt(0).toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>
        )}
        
        <div className="flex-1 space-y-2">
          <div className="relative">
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              autoFocus={autoFocus}
              className={`min-h-[80px] resize-none pr-20 ${
                isOverLimit ? 'border-destructive' : ''
              }`}
              disabled={isSubmitting}
            />
            
            <div className="absolute bottom-2 right-2 flex gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                className="h-6 w-6 p-0"
                disabled={isSubmitting || isUploading}
              >
                {isUploading ? (
                  <Upload size={16} className="animate-spin" />
                ) : (
                  <Image size={16} />
                )}
              </Button>
              
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                className="h-6 w-6 p-0"
                disabled={isSubmitting}
              >
                <Smile size={16} />
              </Button>
            </div>
            
            {showEmojiPicker && (
              <div className="absolute bottom-10 right-2 bg-background border rounded-lg p-3 shadow-lg z-10 w-64">
                <div className="flex gap-1 mb-2 overflow-x-auto">
                  {Object.keys(emojiCategories).map((category) => (
                    <button
                      key={category}
                      type="button"
                      onClick={() => setSelectedCategory(category as keyof typeof emojiCategories)}
                      className={`px-2 py-1 rounded text-xs whitespace-nowrap ${
                        selectedCategory === category 
                          ? 'bg-primary text-primary-foreground' 
                          : 'hover:bg-muted'
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-8 gap-1 max-h-24 overflow-y-auto">
                  {emojiCategories[selectedCategory].map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => handleEmojiClick(emoji)}
                      className="hover:bg-muted p-1 rounded text-lg"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Image Attachments Preview */}
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {attachments.map((attachment, index) => (
                <div key={index} className="relative group">
                  <img 
                    src={attachment.url} 
                    alt={attachment.name}
                    className="w-16 h-16 object-cover rounded border"
                  />
                  <button
                    type="button"
                    onClick={() => removeAttachment(index)}
                    className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
          
          <div className="flex items-center justify-between">
            <span className={`text-xs ${
              isOverLimit ? 'text-destructive' : 'text-muted-foreground'
            }`}>
              {characterCount}/{maxLength}
            </span>
            
            <Button
              type="submit"
              disabled={!content.trim() || isSubmitting || isOverLimit}
              size="sm"
              className="gap-2"
            >
              <Send size={14} />
              {isSubmitting ? 'Posting...' : buttonText}
            </Button>
          </div>
        </div>
      </div>
      
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={(e) => handleImageUpload(e.target.files)}
        className="hidden"
      />
    </form>
  );
}